import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Header from "./header";
import moment from "moment";
import ApiConfig from "../../common/config/ApiConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLanguage } from "../../common/i18n/LanguageContext";

// Same maps WinloseDetail.jsx uses — kept local rather than shared, matching
// this codebase's existing per-file convention for these lookup tables.
const lottoTypeKeyMap = {
  th_gov: "table_lotto_thai",
  laos_gov: "table_lotto_laos",
  hanoy_PhuQua: "lotto_type_hanoi",
  Yeekee_5: "table_lotto_yeekee5",
  Yeekee_10: "table_lotto_yeekee10",
  Yeekee_15: "table_lotto_yeekee15",
};

const betTypeKeyMap = {
  first: "stake_label_first",
  four_top: "stake_label_four_top",
  four_tod: "stake_label_four_tod",
  three_top: "stake_label_three_top",
  three_front: "stake_label_three_front",
  three_bot: "stake_label_three_bot",
  three_tod: "stake_label_three_tod",
  two_top: "stake_label_two_top",
  two_bot: "stake_label_two_bot",
  run_top: "stake_label_run_top",
  run_bot: "stake_label_run_bot",
  run_top_front: "stake_label_run_top_front",
  run_top_mid: "stake_label_run_top_mid",
  run_top_end: "stake_label_run_top_end",
  run_bot_front: "stake_label_run_bot_front",
  run_bot_mid: "stake_label_run_bot_mid",
  run_bot_end: "stake_label_run_bot_end",
  run_top_big: "stake_label_run_top_big",
  run_top_small: "stake_label_run_top_small",
  run_top_even: "stake_label_run_top_even",
  run_top_odd: "stake_label_run_top_odd",
  run_bot_big: "stake_label_run_bot_big",
  run_bot_small: "stake_label_run_bot_small",
  run_bot_even: "stake_label_run_bot_even",
  run_bot_odd: "stake_label_run_bot_odd",
  run_top_front_even: "stake_label_run_top_front_even",
  run_top_front_odd: "stake_label_run_top_front_odd",
  run_top_front_small: "stake_label_run_top_front_small",
  run_top_front_big: "stake_label_run_top_front_big",
  run_top_mid_even: "stake_label_run_top_mid_even",
  run_top_mid_odd: "stake_label_run_top_mid_odd",
  run_top_mid_small: "stake_label_run_top_mid_small",
  run_top_mid_big: "stake_label_run_top_mid_big",
  run_top_end_even: "stake_label_run_top_end_even",
  run_top_end_odd: "stake_label_run_top_end_odd",
  run_top_end_small: "stake_label_run_top_end_small",
  run_top_end_big: "stake_label_run_top_end_big",
  run_bot_front_even: "stake_label_run_bot_front_even",
  run_bot_front_odd: "stake_label_run_bot_front_odd",
  run_bot_front_small: "stake_label_run_bot_front_small",
  run_bot_front_big: "stake_label_run_bot_front_big",
  run_bot_mid_even: "stake_label_run_bot_mid_even",
  run_bot_mid_odd: "stake_label_run_bot_mid_odd",
  run_bot_mid_small: "stake_label_run_bot_mid_small",
  run_bot_mid_big: "stake_label_run_bot_mid_big",
  run_bot_end_even: "stake_label_run_bot_end_even",
  run_bot_end_odd: "stake_label_run_bot_end_odd",
  run_bot_end_small: "stake_label_run_bot_end_small",
  run_bot_end_big: "stake_label_run_bot_end_big",
  step_front_mid_end: "stake_label_step_front_mid_end",
  step_mix: "stake_label_step_mix",
};

const toNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatAmount = (value) => {
  const num = parseFloat(value);
  return isNaN(num)
    ? ""
    : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

// Only senior/master/agent are shown (no company/member group columns) —
// the member's own share is the separate Comm/Net Amt columns instead. Each
// tier shows its position_taking_amount (this bet line's cut for that tier)
// plus its position_taking_percent as a small hint underneath.
const buildTierCell = (pt) =>
  pt && {
    amount: toNumber(pt.position_taking_amount),
    pct: toNumber(pt.position_taking_percent),
  };

const buildRows = (betLines) =>
  (betLines || []).map((line) => {
    const positionTakings = line.positionTakings || [];
    const senior = positionTakings.find((pt) => pt.position === "senior");
    const master = positionTakings.find((pt) => pt.position === "master");
    const agent = positionTakings.find((pt) => pt.position === "agent");

    return {
      key: `${line.order_id}-${line.id}`,
      drawDate: line.resultdate,
      market: line.type,
      ticketNo: line.id,
      transactionTime: line.buydate,
      player: line.member,
      game: line.bet_type,
      number: line.number,
      rewards: toNumber(line.bet_payrate),
      stake: toNumber(line.bet_amout),
      senior: buildTierCell(senior),
      master: buildTierCell(master),
      agent: buildTierCell(agent),
      comm: toNumber(line.member_commission_amount),
      netAmt: toNumber(line.member_net_amount),
    };
  });

const ZERO_TOTALS = {
  senior: { positionTakingAmount: 0 },
  master: { positionTakingAmount: 0 },
  agent: { positionTakingAmount: 0 },
  member: { commissionAmount: 0, netAmt: 0, stake: 0 },
};

function OutstandingDetail() {
  const { t } = useLanguage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get("username");
  const startdate = searchParams.get("startdate") || "";
  const enddate = searchParams.get("enddate") || "";

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState(ZERO_TOTALS);

  useEffect(() => {
    const params = new URLSearchParams({
      user: username,
      startdate,
      enddate,
      page,
      limit,
    });
    ApiConfig.get(`/orderhistory?${params.toString()}`).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          autoClose: 2000,
        });
        return;
      }
      setRows(buildRows(result.data));
      setTotal(result.total ?? 0);
      setTotals(result.totals || ZERO_TOTALS);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, startdate, enddate, page, limit]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const TIER_COLUMNS = [
    { key: "senior", labelKey: "winlose_col_senior" },
    { key: "master", labelKey: "winlose_col_master" },
    { key: "agent", labelKey: "winlose_col_agent" },
  ];

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="shrink-0">
          <Header
            title={`${t("outstanding_detail_page_title")} - ${username ?? ""}`}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-collapse whitespace-nowrap">
                <thead>
                  <tr className="bg-brand-blue-800 text-white">
                    <th className="p-2 text-center">#</th>
                    <th className="p-2 text-left">
                      {t("lottobet_draw_date_label")}
                    </th>
                    <th className="p-2 text-left">
                      {t("lottobet_market_label")}
                    </th>
                    <th className="p-2 text-left">
                      {t("orderhistory_col_ticket_no")}
                    </th>
                    <th className="p-2 text-left">
                      {t("orderhistory_col_transaction_time")}
                    </th>
                    <th className="p-2 text-left">{t("common_member")}</th>
                    <th className="p-2 text-left">
                      {t("lottobet_bet_type_label")}
                    </th>
                    <th className="p-2 text-left">
                      {t("common_number_label")}
                    </th>
                    <th className="p-2 text-right">
                      {t("waitresult_payout_rate_label")}
                    </th>
                    <th className="p-2 text-right">
                      {t("winlose_col_bet_amount")}
                    </th>
                    <th className="p-2 text-right">
                      {t("orderhistory_col_turnover")}
                    </th>
                    {TIER_COLUMNS.map((tier) => (
                      <th key={tier.key} className="p-2 text-right">
                        {t(tier.labelKey)}
                      </th>
                    ))}
                    <th className="p-2 text-right">
                      {t("outstanding_detail_col_comm")}
                    </th>
                    <th className="p-2 text-right">
                      {t("orderhistory_col_net_amt")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9 + TIER_COLUMNS.length + 4}
                        className="text-center py-6 text-slate-400"
                      >
                        {t("common_no_data")}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr
                        key={row.key}
                        className={
                          index % 2 === 0
                            ? "bg-white hover:bg-brand-blue-50"
                            : "bg-gray-50 hover:bg-brand-blue-50"
                        }
                      >
                        <td className="p-2 text-center text-slate-500">
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td className="p-2">
                          {row.drawDate
                            ? moment(row.drawDate).format("YYYY-MM-DD")
                            : ""}
                        </td>
                        <td className="p-2">
                          {lottoTypeKeyMap[row.market]
                            ? t(lottoTypeKeyMap[row.market])
                            : row.market}
                        </td>
                        <td className="p-2">{row.ticketNo}</td>
                        <td className="p-2">
                          {row.transactionTime
                            ? moment(row.transactionTime).format(
                                "YYYY-MM-DD HH:mm:ss",
                              )
                            : ""}
                        </td>
                        <td className="p-2 font-bold">{row.player}</td>
                        <td className="p-2">
                          {betTypeKeyMap[row.game]
                            ? t(betTypeKeyMap[row.game])
                            : row.game}
                        </td>
                        <td className="p-2 font-bold">{row.number}</td>
                        <td className="p-2 text-right">
                          {formatAmount(row.rewards)}
                        </td>
                        <td className="p-2 text-right font-bold text-sky-600">
                          {formatAmount(row.stake)}
                        </td>
                        <td className="p-2 text-right">
                          {formatAmount(row.stake)}
                        </td>
                        {TIER_COLUMNS.map((tier) => (
                          <td key={tier.key} className="p-2 text-right">
                            <div className="font-bold text-slate-700">
                              {formatAmount(row[tier.key]?.amount)}
                            </div>
                            <div className="text-[11px] text-red-500">
                              {row[tier.key]?.pct}%
                            </div>
                          </td>
                        ))}
                        <td className="p-2 text-right">
                          {formatAmount(row.comm)}
                        </td>
                        <td className="p-2 text-right font-bold text-slate-700">
                          {formatAmount(-row.netAmt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr className="bg-brand-blue-50 font-bold">
                      <td
                        colSpan={9}
                        className="p-2 text-center text-brand-blue-800"
                      >
                        {t("table_total")}
                      </td>
                      <td className="p-2 text-right">
                        {formatAmount(totals.member?.stake)}
                      </td>
                      <td className="p-2 text-right">
                        {formatAmount(totals.member?.stake)}
                      </td>
                      {TIER_COLUMNS.map((tier) => (
                        <td key={tier.key} className="p-2 text-right">
                          {formatAmount(totals[tier.key]?.positionTakingAmount)}
                        </td>
                      ))}
                      <td className="p-2 text-right">
                        {formatAmount(totals.member?.commissionAmount)}
                      </td>
                      <td className="p-2 text-right">
                        {formatAmount(-totals.member?.netAmt)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div className="flex items-center justify-between p-2 border-t border-slate-200">
              <span className="text-sm font-semibold text-brand-blue-700">
                {page}/{totalPages} {t("commission_page_label")}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                  className="px-2 py-1 text-sm font-bold rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t("common_first")}
                </button>
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1 text-sm font-medium rounded bg-brand-blue-100 text-brand-blue-700 hover:bg-brand-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t("common_page_previous")}
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1 text-sm font-medium rounded bg-brand-blue-100 text-brand-blue-700 hover:bg-brand-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t("common_next")}
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  className="px-2 py-1 text-sm font-bold rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t("common_last")}
                </button>
                <select
                  className="p-1 border border-gray-300 rounded text-sm ml-1"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OutstandingDetail;
